import { useQuery } from "@tanstack/react-query";
import { abroadBrdApi } from "../../api/abroadLoans";
import { EmptyState, Loading, Modal, Property, Tag } from "../ui";
import { formatMoney } from "../scholarships/funding";
import { formatDateTime, formatLocalDateTime } from "../scholarships/review/model";
import { formatKigaliDate } from "../scholarships/model";
import { paymentKindOf } from "../scholarships/brd/model";
import { AWARDS_KEY } from "./awards";

const answerOf = (award) =>
  award.accepted == null ? (
    <Tag color="yellow">Waiting for the student</Tag>
  ) : award.accepted ? (
    <span>
      <Tag color="green">Accepted</Tag> {formatDateTime(award.respondedAt)}
    </span>
  ) : (
    <span>
      <Tag color="red">Declined</Tag> {formatDateTime(award.respondedAt)}
      {award.declineReason && <span className="ml-1 font-normal italic">“{award.declineReason}”</span>}
    </span>
  );

const travelOf = (award) =>
  award.travelSubmittedAt ? `Sent ${formatDateTime(award.travelSubmittedAt)}${award.waitingForVisa ? " · visa not uploaded yet" : ""}` : award.accepted ? "Not sent yet" : "—";

const ticketOf = ({ ticket }) =>
  ticket ? `${[ticket.airline, ticket.flightNumber].filter(Boolean).join(" ")} · departs ${formatLocalDateTime(ticket.departureAt)} · issued ${formatDateTime(ticket.issuedAt)}` : "Not issued yet";

/**
 * What happened to an approved abroad loan after the Minister, for the HEC Admin, the DG and the
 * Minister to follow: the student's answer, travel, BRD's ticket and payments. Read-only.
 */
export default function AwardProgressDialog({ applicationId, studentName, onClose }) {
  const { data: award, isPending, isError, refetch } = useQuery({
    queryKey: [...AWARDS_KEY, "staff", applicationId],
    enabled: Boolean(applicationId),
    queryFn: async () => {
      try {
        return (await abroadBrdApi.award(applicationId)).data;
      } catch (error) {
        console.error(`Failed to fetch the award of abroad application ${applicationId}:`, error);
        throw error;
      }
    },
  });

  const payments = award?.payments.filter((p) => !p.removedAt) ?? [];
  const currency = award?.approvedAmounts?.currency;

  return (
    <Modal open={Boolean(applicationId)} onClose={onClose} size="lg" title={`Loan progress: ${studentName ?? ""}`} description="After the Minister's approval, as the student and BRD record it">
      {isPending ? (
        <Loading label="Loading the loan's progress…" />
      ) : isError ? (
        <EmptyState title="Could not load the loan's progress" onRefresh={refetch} />
      ) : (
        <div className="flex flex-col gap-5">
          <div>
            <Property label="Approval letter" value={award.letterReference} />
            <Property label="Student's answer" value={answerOf(award)} />
            <Property label="Travel details" value={travelOf(award)} />
            <Property label="Ticket" value={ticketOf(award)} />
            <Property label="Tuition paid" value={`${formatMoney(award.tuitionPaid, currency)} of ${formatMoney(award.approvedAmounts?.tuitionFee, currency)}`} />
            <Property label="Allowances sent" value={formatMoney(award.allowancesSent, currency)} />
          </div>
          <div className="text-sm">
            <p className="mb-2 font-medium text-slate-800">BRD's payments</p>
            {payments.length ? (
              <ul className="flex flex-col gap-1 text-slate-700">
                {payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 pb-1">
                    <span>{p.kind === "ALLOWANCE" && p.embassy ? `Allowance sent to the ${p.embassy}` : paymentKindOf(p.kind, true)}</span>
                    <span>
                      <span className="font-medium text-slate-800">{formatMoney(p.amount, p.currency)}</span> · {formatKigaliDate(p.paidOn)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500">No payment yet.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
